/**
 * Toast notification system — Talk2 glass pill (light theme).
 *
 * Usage: addToast(message, type)
 * Types: 'info' | 'success' | 'error' | 'warning'
 * Auto-dismisses after 3.5s
 */
import React from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from 'lucide-react';
import useStore from '../store/useStore';

const typeConfig = {
  success: {
    icon: CheckCircle,
    accent: '#34C759',
    tint: '#E5F9EC',
  },
  error: {
    icon: AlertCircle,
    accent: '#FF3B30',
    tint: '#FFE5EC',
  },
  warning: {
    icon: AlertTriangle,
    accent: '#FF9500',
    tint: '#FFF3DE',
  },
  info: {
    icon: Info,
    accent: '#007AFF',
    tint: '#E6F1FF',
  },
};

function Toast({ toast }) {
  const removeToast = useStore(s => s.removeToast);
  const cfg = typeConfig[toast.type] || typeConfig.info;
  const Icon = cfg.icon;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 rounded-md max-w-sm w-full pointer-events-auto"
      style={{
        background: 'rgba(255,255,255,0.92)',
        WebkitBackdropFilter: 'saturate(180%) blur(30px)',
        backdropFilter: 'saturate(180%) blur(30px)',
        border: `1px solid rgba(0,0,0,0.06)`,
        boxShadow: '0 20px 50px rgba(0,0,0,.14), 0 4px 12px rgba(0,0,0,.06)',
        borderLeft: `3px solid ${cfg.accent}`,
      }}
      role="alert"
    >
      <div
        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center"
        style={{ background: cfg.tint, color: cfg.accent }}
      >
        <Icon size={15} />
      </div>
      <p className="flex-1 text-sm font-medium text-n-800 leading-snug pt-0.5">{toast.message}</p>
      <button
        onClick={() => removeToast(toast.id)}
        className="text-n-400 hover:text-n-700 transition-colors flex-shrink-0 mt-0.5"
      >
        <X size={14} />
      </button>
    </div>
  );
}

export default function ToastContainer() {
  const toasts = useStore(s => s.toasts);

  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none"
      aria-live="polite"
    >
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
